import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface XRayMaterialProps {
  color: string;
  opacity?: number;
}

export const XRayMaterial: React.FC<XRayMaterialProps> = ({ color, opacity = 1.0 }) => {
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const threeColor = new THREE.Color(color);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uMouse.value.copy(state.pointer);
    }
  });

  const uniforms = React.useMemo(() => ({
    uMouse: { value: new THREE.Vector2(0, 0) },
    uRadius: { value: 0.18 },
    uColor: { value: threeColor },
    uOpacity: { value: opacity }
  }), [threeColor, opacity]);

  return (
    <shaderMaterial
      ref={materialRef}
      transparent
      depthWrite={true}
      uniforms={uniforms}
      vertexShader={`
        varying vec2 vScreenPos;
        varying vec3 vNormal;
        varying vec3 vViewPosition;

        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          vNormal = normalize(normalMatrix * normal);
          vViewPosition = -mvPosition.xyz;
          
          vec4 clipPos = projectionMatrix * mvPosition;
          vScreenPos = clipPos.xy / clipPos.w;
          gl_Position = clipPos;
        }
      `}
      fragmentShader={`
        uniform vec2 uMouse;
        uniform float uRadius;
        uniform vec3 uColor;
        uniform float uOpacity;
        varying vec2 vScreenPos;
        varying vec3 vNormal;
        varying vec3 vViewPosition;

        void main() {
          vec3 normal = normalize(vNormal);
          vec3 lightDir = normalize(vec3(0.5, 0.8, 0.75));
          float diffuse = max(dot(normal, lightDir), 0.0);
          float ambient = 0.35;
          vec3 finalColor = uColor * (diffuse * 0.65 + ambient);

          float dist = distance(vScreenPos, uMouse);
          if (dist < uRadius) {
            float alpha = smoothstep(uRadius * 0.5, uRadius, dist);
            gl_FragColor = vec4(finalColor, alpha * uOpacity);
          } else {
            gl_FragColor = vec4(finalColor, uOpacity);
          }
        }
      `}
    />
  );
};
